import React, { useEffect, useState } from "react";
import commonStyles from "../../common/CommonStyles.module.css";
import s from "./ContactsManagement.module.css";
import {
  getAvailableRoles,
  getContactsConfig,
  saveContactsConfig,
} from "../../../services/contactsService";
import { IconArrowUp, IconArrowDown, IconX, IconPlus } from "@tabler/icons-react";

const ContactsManagement = () => {
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const [roles, config] = await Promise.all([
        getAvailableRoles(),
        getContactsConfig(),
      ]);
      setAvailableRoles(Array.isArray(roles) ? roles : []);
      setSelectedIds(Array.isArray(config) ? config : []);
    } catch (error) {
      console.error("Error loading contacts config:", error);
      setErrorMessage("Ошибка при загрузке данных");
    } finally {
      setIsLoading(false);
    }
  };

  const roleById = (id) => availableRoles.find((role) => role.id === id);

  const addRole = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setSuccessMessage("");
  };

  const removeRole = (id) => {
    setSelectedIds((prev) => prev.filter((roleId) => roleId !== id));
    setSuccessMessage("");
  };

  const move = (index, direction) => {
    setSelectedIds((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setSuccessMessage("");
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await saveContactsConfig(selectedIds);
      setSuccessMessage("Настройки контактов сохранены");
    } catch (error) {
      console.error("Error saving contacts config:", error);
      setErrorMessage("Ошибка при сохранении");
    } finally {
      setIsSaving(false);
    }
  };

  const unselectedRoles = availableRoles.filter(
    (role) => !selectedIds.includes(role.id)
  );

  return (
    <div className={commonStyles.pageContainer}>
      <div className={commonStyles.contentContainer}>
        <div className={commonStyles.content}>
          <h2 className={s.title}>Управление контактами</h2>
          <p className={s.hint}>
            Выберите роли, которые нужно показать в разделе «Контакты», и
            задайте их очередность. Для каждой роли будут отображены её
            пользователи (аватар, должность, e-mail, телефон и соцсети).
          </p>

          {isLoading ? (
            <div className={s.centerMessage}>Загрузка…</div>
          ) : (
            <div className={s.layout}>
              <div className={s.column}>
                <h3 className={s.columnTitle}>Порядок отображения</h3>
                {selectedIds.length === 0 ? (
                  <div className={s.emptyBlock}>
                    Роли не выбраны. Добавьте их из списка справа.
                  </div>
                ) : (
                  <ul className={s.selectedList}>
                    {selectedIds.map((id, index) => {
                      const role = roleById(id);
                      if (!role) return null;
                      return (
                        <li key={id} className={s.selectedItem}>
                          <span className={s.orderBadge}>{index + 1}</span>
                          <span className={s.roleName}>
                            {role.displayName || role.name}
                          </span>
                          <div className={s.itemActions}>
                            <button
                              className={s.iconButton}
                              onClick={() => move(index, -1)}
                              disabled={index === 0}
                              aria-label="Выше">
                              <IconArrowUp className={s.icon} />
                            </button>
                            <button
                              className={s.iconButton}
                              onClick={() => move(index, 1)}
                              disabled={index === selectedIds.length - 1}
                              aria-label="Ниже">
                              <IconArrowDown className={s.icon} />
                            </button>
                            <button
                              className={s.iconButtonDanger}
                              onClick={() => removeRole(id)}
                              aria-label="Убрать">
                              <IconX className={s.icon} />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className={s.column}>
                <h3 className={s.columnTitle}>Доступные роли</h3>
                {unselectedRoles.length === 0 ? (
                  <div className={s.emptyBlock}>Все роли добавлены.</div>
                ) : (
                  <ul className={s.availableList}>
                    {unselectedRoles.map((role) => (
                      <li key={role.id} className={s.availableItem}>
                        <span className={s.roleName}>
                          {role.displayName || role.name}
                        </span>
                        <button
                          className={s.addButton}
                          onClick={() => addRole(role.id)}
                          aria-label="Добавить">
                          <IconPlus className={s.icon} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {errorMessage && <div className={s.errorBox}>{errorMessage}</div>}
          {successMessage && (
            <div className={s.successBox}>{successMessage}</div>
          )}

          <div className={s.footer}>
            <button
              className={s.saveButton}
              onClick={handleSave}
              disabled={isSaving || isLoading}>
              {isSaving ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsManagement;
